import React from 'react';
import './about.css';
import ME from '../../assets/me.jpg';
import { FiLayers, FiLock, FiUsers, FiZap } from 'react-icons/fi';
import { profile } from '../../data/profile';

const pillars = [
  {
    icon: <FiZap />,
    title: 'AI in production',
    text: 'LLM applications, RAG and agent workflows built to survive real traffic and real edge cases.',
  },
  {
    icon: <FiLayers />,
    title: 'Distributed by design',
    text: 'Microservices, event-driven workflows and API contracts with failure modes decided up front.',
  },
  {
    icon: <FiLock />,
    title: 'Financial-grade rigour',
    text: 'Payment rails, bank integrations, OAuth2/JWT/RBAC and the observability to prove it works.',
  },
  {
    icon: <FiUsers />,
    title: 'Hands-on leadership',
    text: 'Architecture, code review and mentoring — while still writing and debugging the code.',
  },
];

const facts = [
  { label: 'Based in', value: `${profile.location} · ${profile.timezone}` },
  { label: 'Education', value: 'B.Sc. Software Engineering — AAiT, Addis Ababa University' },
  { label: 'Domains', value: 'Fintech & payments · AI platforms · Enterprise integrations' },
  { label: 'Currently', value: 'Senior AI/ML Engineer · Senior Full-Stack Developer' },
];

const About = () => (
  <section id="about">
    <div className="container">
      <div className="section__head reveal">
        <span className="section__eyebrow">01 — About</span>
        <div className="section__title">
          <h2>Engineer first, lead second — deliberately in that order.</h2>
        </div>
      </div>

      <div className="about__grid">
        <div className="about__media reveal">
          <div className="about__portrait">
            <img src={ME} alt="Fantahun Fekadu" loading="lazy" />
          </div>
          <div className="about__badge">
            <span className="mono">5+ yrs</span>
            <span>production engineering</span>
          </div>
        </div>

        <div className="about__body reveal">
          <p className="about__lead">
            I&apos;m Fantahun — a lead full-stack developer and AI/ML engineer working from{' '}
            {profile.location}, with teams in San Francisco, New York, London and Toronto.
          </p>

          <p>
            My work sits where <strong>AI meets infrastructure</strong>. Day to day that means
            designing LLM orchestration layers that don&apos;t break when a provider does, building
            retrieval pipelines over business-specific knowledge, and wiring those capabilities into
            backends that already carry payments, merchants and enterprise integrations.
          </p>

          <p>
            Before AI became the focus, I spent years on{' '}
            <strong>mission-critical financial systems</strong> — leading architecture and delivery
            at SantimPay, integrating banks, gateways and third-party platforms, and owning the
            root-cause analysis when production went sideways at 2am. That background is why I care
            more about reliability, evaluation and observability than about demos.
          </p>

          <p>
            As a technical lead I break ambiguous requirements into designs and milestones, set code
            review standards, mentor engineers and make the scalability and security calls — while
            staying hands-on across the stack.
          </p>

          <dl className="about__facts">
            {facts.map((fact) => (
              <div key={fact.label} className="about__fact">
                <dt className="mono">{fact.label}</dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <div className="about__pillars">
        {pillars.map((pillar, i) => (
          <article
            key={pillar.title}
            className="card about__pillar reveal"
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <span className="about__pillar-icon">{pillar.icon}</span>
            <h4>{pillar.title}</h4>
            <p>{pillar.text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

export default About;
